/**
 * useLocaleFormat — Tenant-Aware Formatting Hook
 *
 * Provides currency, date, number, and time formatting functions
 * that respect the tenant's regional settings from Zustand store.
 *
 * Usage:
 *   const { formatCurrency, formatDate, formatDateTime, formatNumber } = useLocaleFormat();
 *   <span>{formatCurrency(1500)}</span>      → "₹1,500.00"
 *   <span>{formatDate(someDate)}</span>       → "09/04/2026"
 *   <span>{formatDateTime(someDate)}</span>   → "09/04/2026 04:00 PM"
 *   <span>{formatNumber(15000)}</span>        → "15,000"
 */

import { useMemo } from 'react';
import { useTenant } from '@/stores';
import {
  formatTenantCurrency,
  formatTenantDate,
  formatTenantDateTime,
  formatTenantTime,
  formatTenantNumber,
  getTenantCurrencySymbol,
  getTenantLocale,
  getTenantCurrency,
  getTenantDateFormat,
  getTenantTimeFormat,
} from '@/lib/locale-format';
import { getTenantTimezone } from '@/lib/timezone';

export function useLocaleFormat() {
  const tenant = useTenant();

  // Resolve settings: Zustand (from login) → localStorage fallback → defaults
  const locale = tenant?.locale ?? getTenantLocale();
  const currency = tenant?.currency ?? getTenantCurrency();
  const timezone = tenant?.timezone ?? getTenantTimezone();
  const dateFormat = tenant?.dateFormat ?? getTenantDateFormat();
  const timeFormat = tenant?.timeFormat ?? getTenantTimeFormat();

  return useMemo(
    () => ({
      /** Format number as currency. e.g. 1500 → "₹1,500.00" */
      formatCurrency: (amount: number | null | undefined, overrideCurrency?: string) =>
        formatTenantCurrency(amount, overrideCurrency ?? currency, locale),

      /** Format date for display. e.g. "2026-04-09T10:30:00Z" → "09/04/2026" */
      formatDate: (date: string | Date | null | undefined) =>
        formatTenantDate(date, dateFormat, timezone),

      /** Format date+time. e.g. "2026-04-09T10:30:00Z" → "09/04/2026 04:00 PM" */
      formatDateTime: (date: string | Date | null | undefined) =>
        formatTenantDateTime(date, dateFormat, timeFormat, timezone),

      /** Format time only. e.g. "2026-04-09T10:30:00Z" → "04:00 PM" */
      formatTime: (date: string | Date | null | undefined) =>
        formatTenantTime(date, timeFormat, timezone),

      /** Format number with locale grouping. e.g. 15000 → "15,000" */
      formatNumber: (num: number | null | undefined, options?: Intl.NumberFormatOptions) =>
        formatTenantNumber(num, locale, options),

      /** Currency symbol. e.g. "₹" */
      currencySymbol: getTenantCurrencySymbol(currency, locale),

      /** Raw settings for custom formatting */
      locale,
      currency,
      timezone,
      dateFormat,
      timeFormat,
    }),
    [locale, currency, timezone, dateFormat, timeFormat],
  );
}
